/**
 * Vera module catalog - single source of truth for the module list shown
 * in BOTH the launcher (LauncherPage) and the compact header picker
 * (ModuleSwitcher). Adding a module surfaces it in both places, so the
 * two can no longer drift (they used to maintain separate hardcoded arrays).
 *
 * The catalog DATA lives in moduleCatalogData.ts (pure, safe for server
 * bundles: admin-config defaults derive from it and the reports-scheduler
 * bundles those defaults). This file adds the browser-only layer: the
 * useAccessibleModules hook, which needs AuthProvider. Do NOT move hooks or
 * provider imports into the data file; that chain crashed the Function App
 * once (2026-08-08, import.meta.env at CJS load).
 *
 * Role/access truth lives in ../../auth/roles (VERA_MODULES, ROLE_PREFIX,
 * hasModuleAccess). The catalog adds the UI metadata (label, icon, color,
 * path, status) those role names don't carry.
 */
import { useAuth } from '../../providers/AuthProvider';
import { hasGlobalAdmin, hasModuleAccess } from '../../auth/roles';
import { MODULE_CATALOG, type ModuleCatalogEntry } from './moduleCatalogData';

export {
  MODULE_CATALOG,
  MODULE_CATEGORIES,
  type ModuleCatalogEntry,
  type ModuleStatus,
  type VeraModuleCategory,
} from './moduleCatalogData';

export interface AccessibleModule {
  module: ModuleCatalogEntry;
  hasAccess: boolean;
}

/**
 * The module catalog filtered for the current user.
 *
 * - `live` modules are returned only when the user has access (module role or
 *   global admin) — `hasAccess` is always `true` for these.
 * - `soon` modules are always returned with `hasAccess: false` so the launcher
 *   can render them as disabled "coming soon" tiles.
 *
 * The launcher renders the whole list; the compact switcher should keep only
 * `live` entries (filter on `module.status === 'live'`).
 */
export function useAccessibleModules(): AccessibleModule[] {
  const { roles } = useAuth();
  const isAdmin = hasGlobalAdmin(roles);

  return MODULE_CATALOG.filter((module) => !module.hidden).map((module) => {
    if (module.status === 'soon') return { module, hasAccess: false };
    if (module.accessWhen) return { module, hasAccess: module.accessWhen(roles) };
    return {
      module,
      hasAccess: module.openToAll || isAdmin || hasModuleAccess(roles, module.rolePrefix),
    };
  }).filter(({ module, hasAccess }) => module.status === 'soon' || hasAccess);
}
